import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Card,
  Avatar,
  Stack,
  Divider,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import {
  AddRounded as AddIcon,
  MoreVertRounded as MoreVertIcon,
  VisibilityRounded as VisibilityIcon,
  EditRounded as EditIcon,
  DeleteRounded as DeleteIcon,
  SendRounded as SendIcon,
  PeopleAltRounded as PeopleIcon,
  PersonAddAltRounded as PersonAddIcon,
  AssignmentIndRounded as AssignmentIndIcon,
  CheckCircleRounded as CheckCircleIcon,
  MailOutlineRounded as MailIcon,
  PhoneRounded as PhoneIcon,
  GroupAddRounded as GroupAddIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import customerService from '../../services/customerService';
import AssignCustomerDialog from '../../components/customers/AssignCustomerDialog';
import { useAuth } from '../../hooks/useAuth';
import PageHeader from '../../components/ui/PageHeader';
import StatCard from '../../components/ui/StatCard';
import StatusChip from '../../components/ui/StatusChip';
import SearchField from '../../components/ui/SearchField';
import EmptyState from '../../components/ui/EmptyState';
import LoadingState from '../../components/ui/LoadingState';

const SOURCE_LABEL = {
  website: 'Website',
  contact: 'Contact',
  assessment: 'Assessment',
  appointment: 'Appointment',
  other: 'Other',
};

const FILTER_LABEL = {
  newThisMonth: 'New this month',
  sentToCrm: 'Sent to CRM',
  unassigned: 'Unassigned',
  total: 'All customers',
};

const MOBILE_PAGE_SIZE = 20;

const CustomersList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(MOBILE_PAGE_SIZE);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    sentToCrm: 0,
    newThisMonth: 0,
    unassigned: 0,
  });

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Selection and dialog state
  const [selectedRows, setSelectedRows] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [assignCustomer, setAssignCustomer] = useState(null);

  // Fetch customers
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        const data = await customerService.getCustomers();
        setCustomers(data);
        setFilteredCustomers(data);

        // Calculate stats
        const now = new Date();
        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

        const calculatedStats = {
          total: data.length,
          sentToCrm: data.filter((customer) => customer.sentToCrm).length,
          newThisMonth: data.filter((customer) => new Date(customer.createdAt) >= firstDayOfMonth)
            .length,
          unassigned: data.filter((customer) => !customer.assignedTo).length,
        };

        setStats(calculatedStats);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
        setError('Failed to load customers. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, []);

  // Filter customers based on search term and active filter
  useEffect(() => {
    let filteredResults = [...customers];

    if (searchTerm.trim()) {
      const lowerCaseSearch = searchTerm.toLowerCase();
      filteredResults = filteredResults.filter(
        (customer) =>
          customer.name?.toLowerCase().includes(lowerCaseSearch) ||
          customer.email?.toLowerCase().includes(lowerCaseSearch) ||
          customer.phone?.toLowerCase().includes(lowerCaseSearch) ||
          customer.address?.toLowerCase().includes(lowerCaseSearch)
      );
    }

    if (activeFilter) {
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      switch (activeFilter) {
        case 'newThisMonth':
          filteredResults = filteredResults.filter(
            (customer) => new Date(customer.createdAt) >= firstDayOfMonth
          );
          break;
        case 'sentToCrm':
          filteredResults = filteredResults.filter((customer) => customer.sentToCrm);
          break;
        case 'unassigned':
          filteredResults = filteredResults.filter((customer) => !customer.assignedTo);
          break;
        default:
          break;
      }
    }

    setFilteredCustomers(filteredResults);
    setMobileVisibleCount(MOBILE_PAGE_SIZE);
  }, [searchTerm, customers, activeFilter]);

  // Handle stats card click (toggles the filter)
  const handleStatsCardClick = (filterType) => {
    if (activeFilter === filterType) {
      setActiveFilter(null);
    } else {
      setActiveFilter(filterType);
    }
  };

  const handleMenuOpen = (event, customer) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedCustomer(customer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewCustomer = (id) => {
    navigate(`/customers/${id}`);
    handleMenuClose();
  };

  const handleEditCustomer = (id) => {
    navigate(`/customers/${id}/edit`);
    handleMenuClose();
  };

  const handleSendToCrm = async (id) => {
    try {
      await customerService.sendToCrm(id);
      setCustomers((prevCustomers) =>
        prevCustomers.map((customer) =>
          customer._id === id ? { ...customer, sentToCrm: true } : customer
        )
      );
      handleMenuClose();
    } catch (err) {
      console.error('Failed to send customer to CRM:', err);
      setError('Failed to send customer to CRM. Please try again.');
    }
  };

  // Delete state + handlers
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleOpenDeleteDialog = (customer) => {
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCustomerToDelete(null);
  };

  const handleDeleteCustomer = async () => {
    if (!customerToDelete) return;

    try {
      setDeleteLoading(true);
      await customerService.deleteCustomer(customerToDelete._id);

      setCustomers((prevCustomers) =>
        prevCustomers.filter((customer) => customer._id !== customerToDelete._id)
      );
      setFilteredCustomers((prevFilteredCustomers) =>
        prevFilteredCustomers.filter((customer) => customer._id !== customerToDelete._id)
      );

      // Recalculate stats
      const updatedCustomers = customers.filter(
        (customer) => customer._id !== customerToDelete._id
      );
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      const calculatedStats = {
        total: updatedCustomers.length,
        sentToCrm: updatedCustomers.filter((customer) => customer.sentToCrm).length,
        newThisMonth: updatedCustomers.filter(
          (customer) => new Date(customer.createdAt) >= firstDayOfMonth
        ).length,
        unassigned: updatedCustomers.filter((customer) => !customer.assignedTo).length,
      };

      setStats(calculatedStats);
      handleCloseDeleteDialog();
      setError(null);
    } catch (err) {
      console.error('Failed to delete customer:', err);
      setError(err.message || 'Failed to delete customer. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Assignment handlers
  const handleOpenAssignDialog = (customer) => {
    setAssignCustomer(customer);
    setAssignDialogOpen(true);
    handleMenuClose();
  };

  const handleOpenBulkAssignDialog = () => {
    if (selectedRows.length === 0) {
      setError('Please select at least one customer to assign');
      return;
    }
    setBulkAssignDialogOpen(true);
  };

  const handleAssignSuccess = () => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        const data = await customerService.getCustomers();
        setCustomers(data);
        setFilteredCustomers(data);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
        setError('Failed to refresh customers. Please reload the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
    setSelectedRows([]);
  };

  const canAssignCustomers = user && (user.role === 'admin' || user.role === 'manager');

  // Data grid columns
  const columns = [
    {
      field: 'name',
      headerName: 'Customer',
      flex: 1.4,
      minWidth: 220,
      renderCell: (params) => (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar
            sx={{
              width: 38,
              height: 38,
              fontSize: '0.9rem',
              bgcolor: alpha(theme.palette.primary.main, 0.14),
              color: 'primary.main',
            }}
          >
            {(params.row.name || '?').charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {params.row.email || '—'}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 0.8,
      minWidth: 130,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {params.row.phone || '—'}
        </Typography>
      ),
    },
    {
      field: 'source',
      headerName: 'Source',
      flex: 0.7,
      minWidth: 120,
      renderCell: (params) => (
        <StatusChip
          status={params.row.source || 'other'}
          color="info"
          label={SOURCE_LABEL[params.row.source] || 'Other'}
        />
      ),
    },
    {
      field: 'sentToCrm',
      headerName: 'CRM',
      flex: 0.7,
      minWidth: 120,
      renderCell: (params) =>
        params.row.sentToCrm ? (
          <StatusChip status="handled" color="success" label="Sent" icon={<CheckCircleIcon />} />
        ) : (
          <StatusChip status="pending" color="warning" label="Not sent" />
        ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      flex: 0.8,
      minWidth: 130,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" noWrap>
            {params.row.createdAt ? format(new Date(params.row.createdAt), 'MMM d, yyyy') : '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {params.row.createdAt ? format(new Date(params.row.createdAt), 'h:mm a') : ''}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      align: 'right',
      width: 96,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="View details">
            <IconButton size="small" onClick={() => handleViewCustomer(params.row._id)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            aria-label="More actions"
            onClick={(event) => handleMenuOpen(event, params.row)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  // Prepare rows for the data grid
  const rows = filteredCustomers.map((customer) => ({
    id: customer._id,
    _id: customer._id,
    name: customer.name,
    email: customer.email,
    phone: customer.phone,
    address: customer.address,
    source: customer.source,
    sentToCrm: customer.sentToCrm,
    assignedTo: customer.assignedTo,
    createdAt: customer.createdAt,
  }));

  if (loading) {
    return <LoadingState label="Loading customers…" />;
  }

  const mobileCustomers = filteredCustomers.slice(0, mobileVisibleCount);

  return (
    <Box>
      <PageHeader
        title="Customers"
        subtitle={`${stats.total} total · ${stats.newThisMonth} new this month`}
        actions={
          <>
            {selectedRows.length > 0 && canAssignCustomers && (
              <Button
                variant="outlined"
                startIcon={<GroupAddIcon />}
                onClick={handleOpenBulkAssignDialog}
              >
                Assign {selectedRows.length} selected
              </Button>
            )}
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/customers/new')}>
              Add Customer
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={{ xs: 1.5, md: 2.5 }} sx={{ mb: { xs: 2.5, md: 3.5 } }}>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Total"
            value={stats.total}
            icon={<PeopleIcon />}
            color="primary"
            selected={activeFilter === 'total'}
            onClick={() => handleStatsCardClick('total')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="New This Month"
            value={stats.newThisMonth}
            icon={<PersonAddIcon />}
            color="info"
            selected={activeFilter === 'newThisMonth'}
            onClick={() => handleStatsCardClick('newThisMonth')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Sent to CRM"
            value={stats.sentToCrm}
            icon={<CheckCircleIcon />}
            color="success"
            selected={activeFilter === 'sentToCrm'}
            onClick={() => handleStatsCardClick('sentToCrm')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Unassigned"
            value={stats.unassigned}
            icon={<AssignmentIndIcon />}
            color="warning"
            selected={activeFilter === 'unassigned'}
            onClick={() => handleStatsCardClick('unassigned')}
          />
        </Grid>
      </Grid>

      {/* Search + active filter */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <SearchField
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, email, phone, or address…"
          sx={{ maxWidth: { sm: 420 } }}
        />
        <Stack direction="row" spacing={1} alignItems="center">
          {activeFilter && (
            <Chip
              label={FILTER_LABEL[activeFilter]}
              onDelete={() => setActiveFilter(null)}
              color="primary"
              variant="outlined"
            />
          )}
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {filteredCustomers.length} result{filteredCustomers.length === 1 ? '' : 's'}
          </Typography>
        </Stack>
      </Stack>

      {filteredCustomers.length === 0 ? (
        <Card>
          <EmptyState
            icon={<PeopleIcon />}
            title={searchTerm || activeFilter ? 'No matching customers' : 'No customers yet'}
            description={
              searchTerm || activeFilter
                ? 'Try adjusting your search or clearing the active filter.'
                : 'Add your first customer to start tracking leads and appointments.'
            }
            action={
              !(searchTerm || activeFilter) && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/customers/new')}>
                  Add Customer
                </Button>
              )
            }
          />
        </Card>
      ) : isMobile ? (
        /* ------------------------- Mobile card list ------------------------- */
        <Stack spacing={1.5}>
          {mobileCustomers.map((customer) => (
            <Card
              key={customer._id}
              onClick={() => handleViewCustomer(customer._id)}
              sx={{
                p: 2,
                cursor: 'pointer',
                transition: 'transform 160ms ease, box-shadow 160ms ease',
                '&:active': { transform: 'scale(0.99)' },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Avatar
                  sx={{
                    width: 44,
                    height: 44,
                    bgcolor: alpha(theme.palette.primary.main, 0.14),
                    color: 'primary.main',
                  }}
                >
                  {(customer.name || '?').charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography variant="subtitle2" noWrap>
                      {customer.name}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="More actions"
                      onClick={(event) => handleMenuOpen(event, customer)}
                      sx={{ mr: -1, mt: -0.5 }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, mt: 0.25 }}>
                    <MailIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {customer.email || '—'}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                    <PhoneIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {customer.phone || '—'}
                    </Typography>
                  </Stack>
                  <Divider sx={{ my: 1.25 }} />
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                    <StatusChip
                      status={customer.source || 'other'}
                      color="info"
                      label={SOURCE_LABEL[customer.source] || 'Other'}
                    />
                    {customer.sentToCrm ? (
                      <StatusChip status="handled" color="success" label="CRM ✓" />
                    ) : (
                      <StatusChip status="pending" color="warning" label="Not in CRM" />
                    )}
                    <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto !important' }}>
                      {customer.createdAt ? format(new Date(customer.createdAt), 'MMM d, yyyy') : ''}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Card>
          ))}
          {mobileVisibleCount < filteredCustomers.length && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setMobileVisibleCount((count) => count + MOBILE_PAGE_SIZE)}
              sx={{ alignSelf: 'center', borderColor: 'divider', px: 4 }}
            >
              Show more ({filteredCustomers.length - mobileVisibleCount} remaining)
            </Button>
          )}
        </Stack>
      ) : (
        /* ------------------------- Desktop data grid ------------------------ */
        <Card sx={{ overflow: 'hidden' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            rowHeight={64}
            checkboxSelection={canAssignCustomers}
            disableRowSelectionOnClick
            onRowDoubleClick={(params) => handleViewCustomer(params.row._id)}
            onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
            rowSelectionModel={selectedRows}
            initialState={{
              pagination: { paginationModel: { pageSize: 15 } },
              sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
            }}
            pageSizeOptions={[10, 15, 25, 50]}
          />
        </Card>
      )}

      {/* Row actions menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        <MenuItem onClick={() => handleViewCustomer(selectedCustomer?._id)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleEditCustomer(selectedCustomer?._id)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        {canAssignCustomers && (
          <MenuItem onClick={() => handleOpenAssignDialog(selectedCustomer)}>
            <ListItemIcon>
              <AssignmentIndIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Assign</ListItemText>
          </MenuItem>
        )}
        {!selectedCustomer?.sentToCrm && (
          <MenuItem onClick={() => handleSendToCrm(selectedCustomer?._id)}>
            <ListItemIcon>
              <SendIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Send to CRM</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => handleOpenDeleteDialog(selectedCustomer)} sx={{ color: 'error.main' }}>
          <ListItemIcon sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Assign dialogs */}
      <AssignCustomerDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        customer={assignCustomer}
        onSuccess={handleAssignSuccess}
      />
      <AssignCustomerDialog
        open={bulkAssignDialogOpen}
        onClose={() => setBulkAssignDialogOpen(false)}
        customerIds={selectedRows}
        isBulkAssign
        onSuccess={handleAssignSuccess}
      />

      {/* Delete confirmation */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Delete customer?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will permanently remove the customer and cannot be undone.
          </Typography>
          {customerToDelete && (
            <Box
              sx={{
                p: 2,
                borderRadius: '12px',
                bgcolor: (t) => alpha(t.palette.error.main, 0.06),
                border: (t) => `1px solid ${alpha(t.palette.error.main, 0.2)}`,
              }}
            >
              <Typography variant="subtitle2">{customerToDelete.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {customerToDelete.email || '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {customerToDelete.phone || '—'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleCloseDeleteDialog} color="inherit" disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteCustomer}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            sx={{ backgroundImage: 'none' }}
          >
            {deleteLoading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomersList;
